import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TaskService } from '../../services/task.service';
import { Task, TaskStatus } from '@contracts/interfaces/task.interface';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { MatTableDataSource } from '@angular/material/table';
import { RunSheetService } from '../../services/run-sheet.service';
import { CustomerDialogService } from '../../dialogs/customer/customer.service.dialog';
import { is_phone_number_a_mobile, phone_number } from '../../tools/phone-number';
import { Clipboard } from '@angular/cdk/clipboard';
import { human } from '../../tools/human-currency';
import { MatDialog } from '@angular/material/dialog';
import { TaskCreateDialog } from '../../dialogs/task-create/task-create.dialog';

interface TaskAgeSection {
  band: number;
  label: string | null;
  tasks: Task[];
}

@Component({
  selector: 'app-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss', '../table.scss'],
})
export class TasksComponent implements OnInit {

  TaskStatus = TaskStatus;
  currentDate: Date = new Date();
  startDate: Date = new Date();
  endDate: Date = new Date();

  activeTab = "active";

  weekOffset: number = 0;

  activeCount = 0;
  todayCount = 0;
  completedCount = 0;
  queuedCount = 0;
  toBePickedUpCount = 0;

  displayedColumns: string[] = [
    'taskNumber',
    'customer',
    'subject',
    'date',
    'action'
  ];

  activeTasks: Task[] = [];
  focusedTasks: Task[] = [];
  queuedTasks: Task[] = [];
  activeTaskAgeSections: TaskAgeSection[] = [];
  focusedTaskAgeSections: TaskAgeSection[] = [];
  queuedTaskAgeSections: TaskAgeSection[] = [];

  isLoading = false;
  showQueued = false;
  copiedActiveTasksToChatGPT = false;

  // For page loading
  doOnce = true;

  activeTasksDataSource: MatTableDataSource<Task>;
  focusedTasksDataSource: MatTableDataSource<Task>;
  queuedTasksDataSource: MatTableDataSource<Task>;

  constructor(
    private taskService: TaskService, 
    private route: ActivatedRoute,
    private runSheetService: RunSheetService,
    private cdr: ChangeDetectorRef,
    private customerDialogService: CustomerDialogService,
    private clipboard: Clipboard,
    private dialog: MatDialog) {}

  ngOnInit(): void {
    this.refreshVisibleTasks();
  }

  private loadTaskCounts() {
    this.taskService.getInfo().subscribe((r: any)=> {
      this.activeCount = r.activeCount;
      this.completedCount = r.completedCount;
      this.queuedCount = r.queuedCount ?? 0;
      this.toBePickedUpCount = r.toBePickedUpCount ?? 0;
    })
  }

  loadActiveTasks() {
    this.taskService.find({status: {
      $nin: [TaskStatus.Completed, TaskStatus.Queued, TaskStatus.ToBePickedUp]
    }}).subscribe((tasks: Task[]) => {
      const active = tasks.reverse();
      const focused = active.filter(t => this.isToday(t));
      const regular = active.filter(t => !this.isToday(t));

      this.setTaskLists(focused, regular);
      
      this.cdr.detectChanges();

      if (this.doOnce) {
        this.route.params.subscribe(params => {
          if (params.taskID != undefined) {
            this.taskService.get(+params.taskID).subscribe((task: Task) => {
              let index = 0;
              for (let i=0; i < this.activeTasks.length;i++) {
                if (this.activeTasks[i].id == task.id) {
                  index = i;
                }
              }

              this.openTaskDialog(task, index);
              this.doOnce = false;
            })
          }
        });
      }
    })
  }

  openTaskDialog(task: Task, currentID: number, taskList: Task[] = this.activeTasks) {
    let numbers: number[] = [];
    for (let task of taskList) {
      numbers.push(task.id);
    }

    window.history.pushState({}, '',`/tasks/${task.id}`);

    this.taskService.openTaskDialog(task, numbers, currentID, (r) => {
      window.history.pushState({}, '',`/tasks`);
      this.refreshVisibleTasks();
    })
  }

  openNewTaskDialog() {
    const dialogRef = this.dialog.open(TaskCreateDialog, {
      width: '760px',
      maxWidth: '95vw',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((created: Task | any) => {
      if (!created?.id) {
        return;
      }

      this.taskService.get(+created.id).subscribe((task: Task) => {
        this.refreshVisibleTasks();
        window.history.pushState({}, '',`/tasks/${task.id}`);
        this.taskService.openTaskDialog(task, [task.id], 0, () => {
          window.history.pushState({}, '',`/tasks`);
          this.refreshVisibleTasks();
        });
      });
    });
  }

  activateTab(tab: string) {
    if (tab == "queued") {
      this.showQueued = !this.showQueued;
      this.refreshVisibleTasks();
      return;
    }

    this.activeTab = tab;
    this.refreshVisibleTasks();
  }

  private refreshVisibleTasks() {
    this.loadTaskCounts();

    switch (this.activeTab) {
      case "today":
        this.loadTodayTasks();
        break;
      case "completed":
        this.loadCompleted();
        break;
      case "pickup":
        this.loadToBePickedUpTasks();
        break;
      default:
        this.loadActiveTasks();
        break;
    }

    if (this.showQueued) {
      this.loadQueuedTasks();
    }
  }

  isToday(task: Task) {
      
    if (task.today != undefined) {
      if (task.today != undefined)
        if (new Date(task.today) > new Date()) {
          return true;
        }
    }

    return false;
  }

  private getTaskAgeBand(task: Task): number {
    const taskDate = new Date(task?.date);
    if (Number.isNaN(taskDate.getTime())) {
      return 0;
    }

    const now = new Date();
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    if (taskDate < sixMonthsAgo) {
      return 3;
    }

    const ageInMilliseconds = now.getTime() - taskDate.getTime();
    const dayInMilliseconds = 24 * 60 * 60 * 1000;

    if (ageInMilliseconds > 14 * dayInMilliseconds) {
      return 2;
    }

    if (ageInMilliseconds > 7 * dayInMilliseconds) {
      return 1;
    }

    return 0;
  }

  private buildTaskAgeSections(tasks: Task[]): TaskAgeSection[] {
    const sections = [0, 1, 2, 3].map((band): TaskAgeSection => ({
      band,
      label: this.getTaskAgeBandLabel(band),
      tasks: tasks.filter(task => this.getTaskAgeBand(task) === band)
    }));

    return sections.filter(section => section.tasks.length > 0);
  }

  private getTaskAgeBandLabel(band: number): string | null {
    switch (band) {
      case 3:
        return 'Over 6 months';
      case 2:
        return 'Over 2 weeks';
      case 1:
        return 'Over 1 week';
      default:
        return null;
    }
  }

  loadTodayTasks() {
    this.isLoading = true;

    this.taskService.find({status: {
      $nin: [TaskStatus.Completed, TaskStatus.Queued, TaskStatus.ToBePickedUp]
    }}).subscribe((tasks: any[]) => {
      const temp: Task[] = [];
      for (let task of tasks) {
        if (task.today != undefined && new Date(task.today) > new Date()) {
          temp.push(task);
        }
      }

      this.focusedTasks = temp.reverse();
      this.setTaskLists(this.focusedTasks, []);
      this.cdr.detectChanges();
      this.isLoading = false;
    });
  }

  loadCompleted() {
    this.isLoading = true;

    this.taskService.find({status: {
      $eq: "completed"
    }}).subscribe((tasks: any[]) => {
      this.focusedTasks = [];
      this.setTaskLists([], tasks.reverse());
      this.cdr.detectChanges();
      this.isLoading = false;
    });
  }

  loadToBePickedUpTasks() {
    this.isLoading = true;

    this.taskService.find({status: TaskStatus.ToBePickedUp}).subscribe((tasks: Task[]) => {
      this.setTaskLists([], (tasks || []).reverse());
      this.cdr.detectChanges();
      this.isLoading = false;
    });
  }

  loadQueuedTasks() {
    this.taskService.find({status: TaskStatus.Queued}).subscribe((tasks: Task[]) => {
      this.queuedTasks = (tasks || []).reverse();
      this.updateTaskDataSources();
      this.cdr.detectChanges();
    });
  }

  toggleToBePickedUp(task: Task) {
    if (!task || task.status === TaskStatus.Completed) {
      return;
    }

    const previousStatus = task.status;
    task.status = this.isToBePickedUp(task)
      ? TaskStatus.InProgress
      : TaskStatus.ToBePickedUp;

    this.taskService.update(task).subscribe({
      next: () => this.refreshVisibleTasks(),
      error: () => {
        task.status = previousStatus;
        this.updateTaskDataSources();
        this.cdr.detectChanges();
      }
    });
  }

  isToBePickedUp(task: Task): boolean {
    return task?.status === TaskStatus.ToBePickedUp;
  }

  private setTaskLists(focused: Task[], active: Task[]) {
    this.focusedTasks = focused;
    this.activeTasks = active;
    this.updateTaskDataSources();
  }

  private updateTaskDataSources() {
    this.focusedTasksDataSource = new MatTableDataSource<Task>(this.focusedTasks);
    this.activeTasksDataSource = new MatTableDataSource<Task>(this.activeTasks);
    this.queuedTasksDataSource = new MatTableDataSource<Task>(this.queuedTasks);
    this.updateTaskAgeSections();
  }

  private updateTaskAgeSections() {
    this.focusedTaskAgeSections = this.buildTaskAgeSections(
      this.focusedTasksDataSource?.filteredData || this.focusedTasks
    );
    this.activeTaskAgeSections = this.buildTaskAgeSections(
      this.activeTasksDataSource?.filteredData || this.activeTasks
    );
    this.queuedTaskAgeSections = this.buildTaskAgeSections(
      this.queuedTasksDataSource?.filteredData || this.queuedTasks
    );
  }

  copyActiveTasksToChatGPT() {
    const payload = [...this.focusedTasks, ...this.activeTasks].map(task => this.buildChatGPTTaskPayload(task));
    this.copiedActiveTasksToChatGPT = this.clipboard.copy(JSON.stringify(payload, null, 2));

    if (this.copiedActiveTasksToChatGPT) {
      setTimeout(() => {
        this.copiedActiveTasksToChatGPT = false;
      }, 2000);
    }
  }

  private buildChatGPTTaskPayload(task: Task) {
    const latestComment = this.getLatestTaskComment(task);
    const customer = task.customer;

    return {
      taskNumber: task.id,
      date: task.date,
      subject: task.subject,
      latestComment: latestComment ? {
        date: latestComment.date,
        text: latestComment.text,
        type: latestComment.type
      } : null,
      customer: customer ? {
        id: customer._id,
        name: customer.name,
        nick: customer.nick,
        business: customer.business,
        owner: customer.owner,
        phone: customer.phone,
        phone2: customer.phone2,
        email: customer.email,
        address: customer.address,
        remoteID: customer.remoteID,
        abn: customer.abn
      } : null
    };
  }

  private getLatestTaskComment(task: Task) {
    const comments = task?.log || [];
    if (!comments.length) {
      return null;
    }

    return comments
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  }

  completeTask(task: Task) {
    const shouldPromptReview = this.shouldPromptForReview(task.customer);
    task.status = TaskStatus.Completed;
    this.taskService.update(task).subscribe(() => {
      this.refreshVisibleTasks();

      if (shouldPromptReview && task.customer?._id) {
        this.customerDialogService.open(
          task.customer,
          '',
          () => {
          },
          (dialogRef) => {
            dialogRef.componentInstance.tab = 'review';
          }
        );
      }
    });
  }

  canTextCustomer(task: Task): boolean {
    return !!this.getTextablePhone(task);
  }

  textCustomer(task: Task, body: string = '') {
    const phone = this.getTextablePhone(task);
    if (!phone) {
      return;
    }

    const message = body ? `?body=${encodeURIComponent(body)}` : '';
    window.location.href = `sms:${this.formatSmsPhone(phone)}${message}`;
  }

  private getTextablePhone(task: Task): string | null {
    const customer = task?.customer;
    if (!customer) {
      return null;
    }

    if (is_phone_number_a_mobile(customer.phone)) {
      return customer.phone;
    }

    if (is_phone_number_a_mobile(customer.phone2)) {
      return customer.phone2;
    }

    return null;
  }

  private formatSmsPhone(phone: string): string {
    const normalized = phone_number(phone);
    if (normalized.substring(0, 2) === '04') {
      return `+61${normalized.substring(1)}`;
    }

    return normalized;
  }

  search (query: string) {
    if (this.activeTasksDataSource) {
      this.activeTasksDataSource.filter = query;
    }
    if (this.focusedTasksDataSource) {
      this.focusedTasksDataSource.filter = query;
    }
    if (this.queuedTasksDataSource) {
      this.queuedTasksDataSource.filter = query;
    }
    this.updateTaskAgeSections();
  }

  searchCancelled() {
    if (this.activeTasksDataSource) {
      this.activeTasksDataSource.filter = null;
    }
    if (this.focusedTasksDataSource) {
      this.focusedTasksDataSource.filter = null;
    }
    if (this.queuedTasksDataSource) {
      this.queuedTasksDataSource.filter = null;
    }
    this.updateTaskAgeSections();
  }

  viewRunSheet() {
    this.runSheetService.openRunSheetDialog({}, null);
  }

  smsBodyJobCompleted(task: Task): string {
    const customer = task.customer;
    if (!customer) {
      return '';
    }

    const name = customer.nick || customer.name || 'there';
    return `Hi ${name}, your ${task.subject} repair is complete and ready for collection.`;
  }

  smsBodyInvoiceAmount(task: Task): string {
    const invoice = task?.invoice;

    return [
      'PayID: 0466040720 (Summer White)',
      `Ref: inv ${invoice.number}`,
      `Amount: ${human(invoice.total)}`,
      '',
      'Or Cash/Card on collection'
    ].join('\n');
  }

  get queuedCountLabel(): string {
    return `${this.queuedCount} queued to start`;
  }

  get primaryGroupTitle(): string {
    switch (this.activeTab) {
      case "completed":
        return "Completed";
      case "pickup":
        return "To be picked up";
      case "queued":
        return "Queued";
      case "today":
        return "Today";
      default:
        return "Active";
    }
  }

  get primaryGroupHint(): string {
    const count = this.activeTasks?.length || 0;
    switch (this.activeTab) {
      case "completed":
        return `${count} finished`;
      case "pickup":
        return `${count} awaiting collection`;
      case "queued":
        return `${count} queued to start`;
      case "today":
        return `${count} marked for today`;
      default:
        return `${count} in progress`;
    }
  }

  private shouldPromptForReview(customer?: CustomerInterface | null): boolean {
    if (!customer?._id) {
      return false;
    }

    const status = customer.googleReviewStatus;
    const hasSentReview =
      status === 'requested' ||
      status === 'left';
    const skipRequested = status === 'do_not_request';
    const hasSmsHistory = Array.isArray(customer.googleReviewSMS) && customer.googleReviewSMS.length > 0;

    return !hasSentReview && !skipRequested && !hasSmsHistory;
  }
}
